import { useLayoutEffect, useRef, useState } from 'react'
import type { Block } from '@/types/blocks'

/** Сколько живёт «уходящая» миниатюра — длина `animate-thumb-out`. */
export const LEAVE_MS = 280

export type StripItem =
  { block: Block; leaving: false } | { block: Block; leaving: true; index: number }

/**
 * Лента блоков с «уходящими» миниатюрами: удалённый блок ещё ~0.3 с стоит на
 * своём месте и схлопывается (`animate-thumb-out`), а соседи плавно съезжают —
 * без скачка. Анимируется ТОЛЬКО удаление: исчез один-два блока и ни одного
 * нового. Смена источника (черновик завёлся — у всех блоков новые id) и замена
 * временной записи настоящим блоком под это правило не попадают.
 *
 * `useLayoutEffect`, а не `useEffect`: уходящая миниатюра обязана встать на
 * место до отрисовки, иначе на один кадр она пропадёт и вернётся.
 */
export const useLeavingBlocks = (blocks: Block[]): StripItem[] => {
  const prev = useRef(blocks)
  const [leaving, setLeaving] = useState<{ block: Block; index: number }[]>([])

  useLayoutEffect(() => {
    const before = prev.current
    prev.current = blocks
    if (before === blocks) return
    const now = new Set(blocks.map((b) => b.id))
    const was = new Set(before.map((b) => b.id))
    if (blocks.some((b) => !was.has(b.id))) return
    const gone = before
      .map((block, index) => ({ block, index }))
      .filter((x) => x.block.id > 0 && !now.has(x.block.id))
    if (gone.length === 0 || gone.length > 2) return
    setLeaving((list) => [...list, ...gone])
    setTimeout(() => setLeaving((list) => list.filter((x) => !gone.includes(x))), LEAVE_MS)
  }, [blocks])

  const items: StripItem[] = blocks.map((block) => ({ block, leaving: false }))
  for (const g of [...leaving].sort((a, b) => a.index - b.index)) {
    items.splice(Math.min(g.index, items.length), 0, { ...g, leaving: true })
  }
  return items
}
